"use client";

import posthog from "posthog-js";
import { useEffect, useRef } from "react";

// Don't pollute the funnel with local dev traffic.
const enabled = () =>
  typeof window !== "undefined" && window.location.hostname !== "localhost";

/** Central event tracker. Use this for every custom event. */
export function track(event: string, properties?: Record<string, unknown>) {
  if (!enabled()) return;
  posthog.capture(event, properties);
}

/**
 * Fires `section_viewed` { section } once, the first time a section scrolls
 * into view. Reaching the features / pricing / FAQ sections is a leading
 * indicator of purchase intent, it lets you build a scroll-depth funnel.
 *
 * Usage: `const ref = useSectionView<HTMLDivElement>("pricing");` then `ref={ref}`.
 */
export function useSectionView<T extends HTMLElement = HTMLElement>(
  section: string,
) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled()) return;
    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!fired && entries.some((e) => e.isIntersecting)) {
          fired = true;
          track("section_viewed", { section });
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [section]);
  return ref;
}
